import React, { useEffect, useRef, useState } from 'react'

import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import Mexp from 'math-expression-evaluator'
import ButtonsContainer from 'features/declaration/form/components/calculator/ButtonsContainer'
import DisplayContainer from 'features/declaration/form/components/calculator/DisplayContainer'

interface CalculatorModalProps {
  isOpen: boolean
  close: () => void
  onResolve: (value: number) => void
}

const sanitizeRegex = /[^0-9+\-*/().^]+/g

function CalculatorModal(props: Readonly<CalculatorModalProps>) {
  const { isOpen, close, onResolve } = props

  const { t } = useTranslation()

  const expressionEvaluator = new Mexp()

  const [expression, setExpression] = useState<string>('')
  const [result, setResult] = useState<number>(0)
  const latestExpressionRef = useRef(expression)

  const clear = () => {
    setExpression('')
    setResult(0)
  }

  const calculate = (expr: string): number => {
    const n = expr.length
    if (n === 0) {
      return 0
    }

    try {
      return expressionEvaluator.eval(expr)
    } catch (e) {
      // console.error(e)
    }
    return 0
  }

  const backspace = () => {
    const newExpression = expression.slice(0, -1)
    setExpression(newExpression)
    setResult(calculate(newExpression))
  }

  const handleInputChange = (e: any) => {
    const value = e.target.value ?? ''
    const newExpression = value.replace(sanitizeRegex, '')
    setExpression(newExpression)
    setResult(calculate(newExpression))
  }

  const replaceExpressionWithCalculatedResult = () => {
    const newResult = calculate(latestExpressionRef.current)
    setExpression(String(newResult))
    setResult(newResult)
  }

  const handleClick = (e: any) => {
    const value = e.currentTarget.name ?? ''
    if (value === 'AC') {
      clear()
      return
    }
    if (value === 'backspace') {
      backspace()
      return
    }
    if (value === '=') {
      replaceExpressionWithCalculatedResult()
      return
    }

    const newExpression = expression + value
    setExpression(newExpression)
    setResult(calculate(newExpression))
  }

  const resolve = () => {
    const newResult = calculate(latestExpressionRef.current)
    setResult(newResult)
    onResolve(newResult)
  }

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    function keyDownHandler(e: globalThis.KeyboardEvent) {
      if (isOpen) {
        if (e.key === 'Escape') {
          e.preventDefault()
          close()
        }
        if (e.key === '=') {
          e.preventDefault()
          replaceExpressionWithCalculatedResult()
        }
        if (e.key === 'Enter') {
          e.preventDefault()
          resolve()
        }
      }
    }

    document.addEventListener('keydown', keyDownHandler)

    return () => {
      document.removeEventListener('keydown', keyDownHandler)
    }
  }, [isOpen])

  useEffect(() => {
    latestExpressionRef.current = expression
  }, [expression])

  return (
    <Modal
      show={isOpen}
      className="overflow-hidden"
      dialogClassName="calculator-modal"
      aria-labelledby="contained-modal-title-vcenter"
      animation={false}
      backdropClassName="backdrop-with-modal"
      centered
      scrollable
    >
      <Modal.Header>
        <Modal.Title className="justify-content-between">
          <h2>{t('calculator.title')}</h2>
          <button
            type="button"
            className="btn btn-link no-underline btn-lg d-flex justify-content-center align-items-center px-0"
            onClick={close}
          >
            <span>{t('buttons.close')}</span>
            <i className="fal fa-times fa-2x ms-2" />
          </button>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <DisplayContainer
          display={expression}
          result={result}
          backspace={backspace}
          handleInputChange={handleInputChange}
        />
        <ButtonsContainer handleClick={handleClick} />
        <div className="d-flex align-items-end justify-content-center mt-4">
          <button
            type="button"
            disabled={result === 0}
            className="btn btn-lg btn-primary mx-1 text-secondary shadow-sm"
            onClick={(resolve)}
          >
            <span>{t('calculator.insert')}</span>
          </button>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default CalculatorModal
